import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, MessageCircleQuestion, Trash2 } from "lucide-react";
import { API } from "../../context/AuthContext";
import { MASCOT_URL } from "../flux/Decor";

const display = "font-display";

export default function AskFlux({ caseId, caseName }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState("all");
  const bottomRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/ask/history`).then((res) => setMessages(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const question = input.trim();
    if (!question || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: question }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ask`, { question, case_id: scope === "case" ? caseId : null });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.answer }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Flux dropped his notes. Ask again!");
      setMessages((prev) => prev.slice(0, -1));
      setInput(question);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      await axios.delete(`${API}/ask/history`);
      setMessages([]);
      toast.success("Conversation wiped.");
    } catch {
      toast.error("Couldn't clear the chat.");
    }
  };

  return (
    <div className="bg-[#131f3a] border border-white/5 p-6 rounded-2xl shadow-xl flex flex-col min-h-[560px] max-h-[720px]" data-testid="askflux-panel">
      <div className="border-b border-white/5 pb-4 mb-4 flex flex-wrap justify-between items-center gap-3">
        <div>
          <h3 className={`${display} font-bold text-base text-white uppercase tracking-wide flex items-center gap-2`}>
            <MessageCircleQuestion className="w-4 h-4 text-[#f59e0b]" /> Ask Professor Flux
          </h3>
          <p className="text-xs text-gray-300 mt-0.5">Ask anything — Flux has read your entire database.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-gray-950 p-1 rounded-lg border border-white/5 flex gap-1 text-[10px] font-mono">
            <button
              onClick={() => setScope("all")}
              data-testid="ask-scope-all"
              className={`px-2.5 py-1.5 rounded font-bold transition-colors duration-200 ${scope === "all" ? "bg-[#f59e0b] text-gray-950" : "text-gray-400 hover:text-white"}`}
            >
              ALL TIMELINES
            </button>
            <button
              onClick={() => setScope("case")}
              data-testid="ask-scope-case"
              className={`px-2.5 py-1.5 rounded font-bold transition-colors duration-200 ${scope === "case" ? "bg-[#f59e0b] text-gray-950" : "text-gray-400 hover:text-white"}`}
              title={caseName}
            >
              THIS TIMELINE
            </button>
          </div>
          {messages.length > 0 && (
            <button onClick={clearChat} data-testid="ask-clear-btn" className="text-gray-500 hover:text-rose-400 border border-white/10 p-2 rounded-lg transition-colors duration-200" title="Clear conversation">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 pr-1">
        {messages.length === 0 && !loading && (
          <div className="text-center py-10 space-y-3" data-testid="ask-empty-state">
            <img src={MASCOT_URL} alt="" className="w-20 h-20 object-contain mx-auto animate-float" />
            <p className="text-xs text-gray-300 italic max-w-sm mx-auto leading-relaxed">
              Try: "When was the first hearing?" · "What happened between the TRO and the settlement conference?" · "Which events involve money?"
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={m.message_id || `local-${i}`} data-testid={`ask-message-${i}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            {m.role === "assistant" && <img src={MASCOT_URL} alt="" className="w-7 h-7 object-contain mr-2 flex-shrink-0 mt-1" />}
            <div className={`max-w-[85%] px-4 py-3 rounded-xl text-[13px] leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-[#f59e0b]/10 border border-[#f59e0b]/20 text-gray-100 rounded-tr-sm" : "bg-white/[0.04] border border-white/5 text-gray-200 rounded-tl-sm"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2">
            <img src={MASCOT_URL} alt="" className="w-7 h-7 object-contain" />
            <span className="text-[#f59e0b] font-mono text-xs animate-blink">Flux is flipping through your records...</span>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="mt-4 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          data-testid="ask-input"
          placeholder="Ask your database anything..."
          className="flex-1 bg-gray-950 border border-white/5 rounded-xl px-4 py-3 text-sm text-gray-200 focus:outline-none focus:border-[#f59e0b] transition-colors duration-200"
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          data-testid="ask-send-btn"
          className={`bg-[#f59e0b] hover:bg-[#fbbf24] disabled:opacity-50 text-gray-950 ${display} font-bold px-5 rounded-xl transition-colors duration-200 active:scale-[0.97] flex items-center gap-2`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
